import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "@/data/brand";
import { SERVICES } from "@/data/services";
import { CITIES } from "@/data/cities";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";
import { AmbientLight } from "@/components/brand/AmbientLight";

const COL = "font-mono text-[0.65rem] text-gold tracking-[0.28em] uppercase mb-6";
const LINK = "block text-ag-text-secondary/70 hover:text-gold text-[0.85rem] py-1.5 transition-colors font-light";

export default function Footer() {
  const topServices = SERVICES.slice(0, 6);
  return (
    <footer className="relative bg-midnight-deeper border-t border-white/5 overflow-hidden" data-testid="site-footer">
      <AmbientLight size={1200} color="blue" intensity={0.2} style={{ bottom: "-30%", left: "50%", transform: "translateX(-50%)" }} />
      <div className="absolute right-0 bottom-0 opacity-30 pointer-events-none" aria-hidden>
        <ConcentricMonogram size={600} variant="ghost" glowColor="none" rotateRings={false} />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-12">
          <div className="col-span-2 lg:col-span-4">
            <Link to="/" className="inline-flex items-start" data-testid="footer-logo">
              <img
                src={BRAND.logoWhite}
                alt="AG Public Adjusting"
                className="h-14 w-auto object-contain"
              />
            </Link>
            <div className="mt-4 font-mono text-[0.6rem] text-gold/80 tracking-[0.32em] uppercase">
              Licensed Public Adjuster Firm
            </div>
            <p className="text-ag-text-secondary/70 text-sm mt-8 leading-[1.7] max-w-md font-light">
              Florida&apos;s institutional public adjusting firm — representing policyholders, exclusively.
              We document, negotiate, and recover insurance claims with the rigor of a modern firm.
            </p>
            <div className="mt-10 space-y-2 text-sm text-ag-text-secondary font-light">
              <div><span className="text-ag-text-muted font-mono text-xs tracking-wider">DIRECT</span><br /><a href={BRAND.phoneHref} className="font-display text-xl text-gold hover:text-gold-light tracking-[-0.02em]" data-testid="footer-phone">{BRAND.phoneDisplay}</a></div>
              <div className="pt-4"><span className="text-ag-text-muted font-mono text-xs tracking-wider">EMAIL</span><br /><a href={BRAND.emailHref} className="hover:text-gold transition-colors" data-testid="footer-email">{BRAND.email}</a></div>
              <div className="pt-4">
                <span className="text-ag-text-muted font-mono text-xs tracking-wider">OFFICE</span><br />
                <a href={BRAND.address.mapsHref} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors leading-[1.5] inline-block" data-testid="footer-address">
                  {BRAND.address.street}<br />
                  {BRAND.address.city}, {BRAND.address.state} {BRAND.address.zip}
                </a>
              </div>
              <div className="pt-4">
                <span className="text-ag-text-muted font-mono text-xs tracking-wider">HOURS</span>
                <div className="mt-1 space-y-0.5 text-[0.78rem] font-light text-ag-text-secondary/85" data-testid="footer-hours">
                  {BRAND.hours.map((h) => (
                    <div key={h.day} className="leading-[1.45]">
                      <span className="text-white/70">{h.day}</span>
                      <span className="text-ag-text-muted"> · </span>
                      <span>{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-6 font-mono text-[0.6rem] text-ag-text-muted tracking-[0.22em] uppercase">{BRAND.licenseNumber}</div>
            </div>
          </div>

          <div className="lg:col-span-2 lg:col-start-6">
            <h4 className={COL}>The Firm</h4>
            <Link to="/the-firm" className={LINK} data-testid="footer-link-firm">Our Approach</Link>
            <Link to="/process" className={LINK} data-testid="footer-link-process">The AG Method</Link>
            <Link to="/insights" className={LINK} data-testid="footer-link-insights">Resource Center</Link>
            <Link to="/contact" className={LINK} data-testid="footer-link-contact">Contact</Link>
            <Link to="/digital-card/login" className={`${LINK} text-gold`} data-testid="footer-link-card-portal">Card Portal →</Link>
          </div>

          <div className="lg:col-span-3">
            <h4 className={COL}>Claim Services</h4>
            {topServices.map((s) => (
              <Link key={s.slug} to={`/claim-services/${s.slug}`} className={LINK} data-testid={`footer-service-${s.slug}`}>
                {s.title}
              </Link>
            ))}
            <Link to="/claim-services" className={`${LINK} text-gold mt-2`} data-testid="footer-service-all">
              View all 15 services →
            </Link>
          </div>

          <div className="lg:col-span-2">
            <h4 className={COL}>Florida Coverage</h4>
            {CITIES.map((c) => (
              <Link key={c.slug} to={`/florida-coverage/${c.slug}`} className={LINK} data-testid={`footer-city-${c.slug}`}>
                {c.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-ag-text-muted text-xs font-light leading-relaxed max-w-2xl" data-testid="footer-copyright">
            © {new Date().getFullYear()} AG Public Adjusting. All rights reserved. AG Public Adjusting represents policyholders exclusively. AG Public Adjusting does not represent insurance carriers.
          </p>
          <div className="flex items-center gap-6 font-mono text-[0.65rem] text-ag-text-muted tracking-[0.22em] uppercase">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/digital-card/admin" className="hover:text-white transition-colors" data-testid="footer-link-admin-crm">Admin</Link>
            <span className="text-gold/70">FL · USA</span>
          </div>
        </div>

        {/* Powered by Eternity MP */}
        <div className="mt-6 flex justify-center md:justify-end" data-testid="footer-credit-wrap">
          <a
            href={BRAND.credit.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit"
            className="group inline-flex items-center gap-2 font-mono text-[0.6rem] tracking-[0.28em] uppercase text-ag-text-muted/70 hover:text-gold transition-colors"
          >
            <span>Powered by</span>
            <span className="gold-metallic font-medium tracking-[0.32em] group-hover:tracking-[0.36em] transition-[letter-spacing] duration-500">
              {BRAND.credit.company}
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
}
